import React, { PureComponent } from 'react';
import _ from 'underscore';
import utils from '../../utils';
import Annotation from './highlight/annotation.jsx';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import R from 'ramda';
import HTMLPopover from './highlight/popover';
import TetherComponent from '../tether';
import connect from '../connect';
import { isMine, getAnswerFor } from '../../selectors/markup';
import {
    isInstructorInSection,
    canAskQuestion,
} from '../../selectors/current_user';
import { selectSection } from '../../selectors/section';
import { selectContent } from '../../selectors/content';
import { deleteMarkup, update } from '../../actions/markup';
import { isConnected } from '../../selectors/offline';
import env from '../../env';
import ReadOnly from 'components/text_viewer/highlight/readonly';
import Mine from 'components/text_viewer/highlight/mine.jsx';

const { EDIT_HIGHLIGHTS } = env;

const highlightType = PropTypes.shape({
    selections(props, propName, componentName) {
        const selections = props[propName];
        if (!Array.isArray(selections)) {
            return new Error(componentName + ': selections must be an array');
        }
        if (selections.length === 0) {
            return new Error(componentName + ': selections must not be empty');
        }
    },
    color: PropTypes.string.isRequired,
    selection_text: PropTypes.string.isRequired,
});

class Highlight extends PureComponent {
    static Annotation = Annotation;

    static defaultProps = {
        useHtmlPopover: false,
    };

    static displayName = 'Highlight';
    static highlightType = highlightType;

    static propTypes = {
        update: PropTypes.func.isRequired,
        deleteMarkup: PropTypes.func.isRequired,
        highlight: highlightType.isRequired,
        onSelect: PropTypes.func.isRequired,
        useHtmlPopover: PropTypes.bool.isRequired,
        answer: highlightType,
        hideSharingQA: PropTypes.bool.isRequired,
        isMine: PropTypes.bool.isRequired,
        isConnected: PropTypes.bool.isRequired,
        currentUserIsInstructor: PropTypes.bool.isRequired,
        canAskQuestion: PropTypes.bool.isRequired,
        isQuestion: PropTypes.bool.isRequired,
    };

    id = _.uniqueId('Highlight');

    state = {
        popoverOpen: false,
    };

    onClick = e => {
        if (this.isInteractive()) {
            e.preventDefault();
            e.stopPropagation();
            const nextState = {
                popoverOpen: !this.state.popoverOpen,
            };

            if (nextState.popoverOpen && this.props.onSelect) {
                // When opening make sure to select the text.
                this.props.onSelect(this.props.highlight);
            }

            this.setState(nextState);
        }
    };
    onContextMenu = e => {
        if (this.isEditable()) {
            e.preventDefault();
            this.editHighlight();
        }
    };

    editHighlight() {
        if (this.isEditable()) {
            this.props.onSelect(this.props.highlight, 'edit');
            this.setState({ popoverOpen: false });
        }
    }

    isEditable() {
        return this.isInteractive() && this.props.isMine && EDIT_HIGHLIGHTS;
    }

    isInteractive() {
        return this.props.useHtmlPopover;
    }

    renderAnnotation() {
        const highlight = this.props.highlight;

        if (highlight.note_text) {
            const selection = R.head(highlight.selections);
            const tly = selection[1];
            const brx = selection[2];
            const lineHeight = selection[3] - selection[1];

            let type = 'note';
            if (highlight.is_question) {
                type = 'question';

                if (this.props.answer) {
                    type = 'answered-question';
                }
            }

            return (
                <Annotation
                    cx={brx}
                    cy={tly}
                    r={lineHeight / 2}
                    type={type}
                    color={highlight.color}
                />
            );
        }
        return false;
    }
    renderHtmlPopover() {
        if (this.state.popoverOpen) {
            const highlight = this.props.highlight;
            const close = function() {
                this.setState({ popoverOpen: false });
            }.bind(this);

            if (!this.props.isConnected) {
                return this.renderReadOnly(close);
            }

            // This is just a copy of the existing behavior. I can't explain
            // why it's this way.
            //
            // https://github.com/courseload/engage/blob/1a4040cbe576a018dfdcbdecc6469782b3e0a7a2/js/views/highlight_popover.coffee#L65-L66
            if (highlight.is_question) {
                if (
                    this.props.answer ||
                    // ¯\_(ツ)_/¯
                    this.props.currentUserIsInstructor
                ) {
                    return this.renderQuestion(close);
                }
            }

            if (!this.props.isMine) {
                return this.renderReadOnly(close);
            }
            return this.renderMine(close);
        }
    }
    renderMine(close) {
        // eslint-disable-line react/display-name
        const {
            update,
            highlight,
            canAskQuestion,
            deleteMarkup,
            isQuestion,
            hideSharingQA,
        } = this.props;
        return (
            <TetherComponent target={'#' + this.id}>
                <HTMLPopover>
                    <Mine
                        id={highlight.id}
                        update={update}
                        canAskQuestion={canAskQuestion}
                        isQuestion={isQuestion}
                        deleteMarkup={deleteMarkup}
                        highlight={highlight}
                        close={close}
                        hideSharingQA={hideSharingQA}
                    />
                </HTMLPopover>
            </TetherComponent>
        );
    }

    renderQuestion(close) {
        // eslint-disable-line react/display-name
        const Question = require('./highlight/question.jsx').default;

        return (
            <TetherComponent target={'#' + this.id}>
                <HTMLPopover>
                    <Question markup={this.props.highlight} close={close} />
                </HTMLPopover>
            </TetherComponent>
        );
    }
    renderReadOnly(close) {
        // eslint-disable-line react/display-name

        return (
            <TetherComponent target={'#' + this.id}>
                <HTMLPopover>
                    <ReadOnly markup={this.props.highlight} close={close} />
                </HTMLPopover>
            </TetherComponent>
        );
    }

    renderSelections() {
        const highlight = this.props.highlight;

        const selections = highlight.selections;

        return selections
            .map(function(selection, key) {
                const tlx = selection[0];
                const tly = selection[1] + 1; // Nudge highlight down a bit
                const brx = selection[2];
                const bry = selection[3] + 3; // Make highlight a bit taller
                return (
                    <rect
                        key={key}
                        className={classnames('selectedword', highlight.color)}
                        width={brx - tlx}
                        height={bry - tly}
                        x={tlx}
                        y={tly}
                    />
                );
            })
            .concat(
                selections.map(function(selection, key) {
                    const tlx = selection[0];
                    const tly = selection[1] + 1;
                    const brx = selection[2];
                    const bry = selection[3] + 3;
                    return (
                        <line
                            key={'line' + key}
                            className={classnames(
                                'highlight-underline',
                                highlight.color
                            )}
                            x1={tlx}
                            y1={tly + (bry - tly)}
                            x2={brx}
                            y2={bry}
                        />
                    );
                })
            );
    }

    render() {
        const highlight = this.props.highlight;
        const selections = highlight.selections;

        const boundingBox = selections.reduce((memo, selection) => {
            const tlx = selection[0];
            const tly = selection[1];

            return {
                x: _.min([memo.x, tlx]),
                y: _.min([memo.y, tly]),
            };
        }, {});

        // If you insert this into `<text>` below react would try to create a
        // span around the selection_text, but that isn't valid in an svg
        const auralText = `${highlight.color} highlighted text:
            ${highlight.selection_text}`;

        const i = this.isInteractive();
        const selectionClasses = classnames({
            'selection-container': true,
            interactive: i,
        });

        return (
            <g
                className="highlight"
                data-test={`highlight_${highlight.id}`}
                onContextMenu={i ? this.onContextMenu : undefined}
                onClick={i ? this.onClick : undefined}
                onKeyDown={i ? utils.ifSpace(this.onClick) : undefined}
                role={i ? 'button' : undefined}
                // tabIndex={i ? '0' : undefined}
                // TODO: make sure this is still working
                // onTap={i ? this.onTap : undefined}
            >
                <text x={boundingBox.x} y={boundingBox.y} fontSize="0px">
                    {auralText}
                </text>
                <g className={selectionClasses} id={this.id}>
                    {this.renderSelections()}
                </g>
                {this.renderAnnotation()}
                {this.renderHtmlPopover()}
            </g>
        );
    }
}

function mapStateToProps(state, props) {
    const { highlight } = props;
    const { content_id, is_question } = highlight;
    const content = selectContent(state, content_id);
    const { section_id } = content;
    const section = selectSection(state, section_id);

    const answer = is_question ? getAnswerFor(state, highlight) : undefined;
    const answered = is_question ? !!answer : false;
    return {
        answer,
        canAskQuestion: !answered && canAskQuestion(state, content),
        isMine: isMine(state, highlight),
        currentUserIsInstructor: isInstructorInSection(state, section),
        isConnected: isConnected(state),
        isQuestion: is_question,
    };
}

// This probably shouldn't be a connected component
export default connect(
    mapStateToProps,
    {
        deleteMarkup,
        update,
    }
)(Highlight);



// WEBPACK FOOTER //
// ./src/components/text_viewer/highlight.jsx